import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiMinus } from 'react-icons/fi';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "My child has an IEP. Is this a replacement?",
      answer: "Absolutely not. We are a crucial complement to your IEP. The IEP ensures accommodations and access to education. We provide the therapy to build the underlying skills so your child may eventually need fewer accommodations. We work alongside your school team."
    },
    {
      question: "How is this different for NVLD vs. Dyslexia?",
      answer: "While both involve EF deficits, the focus differs. For NVLD, we heavily target visual-spatial reasoning, social inference, and cognitive flexibility. For dyslexia, we integrate phonological awareness with working memory and processing speed training. The therapy is personalized to the profile."
    },
    {
      question: "My child won't sit still for traditional therapy. Will this work?",
      answer: "Yes! We meet them where they are and use their natural curiosity to build brain skills."
    },
    {
      question: "Why speech therapy for learning disorders?",
      answer: "Speech-language pathologists are uniquely trained in cognitive-communication skills, language processing, and executive function. We understand how the brain processes information and can address the full spectrum of learning challenges."
    },
    {
      question: "What if we don't have a formal diagnosis?",
      answer: "You don't need a diagnosis to benefit from executive function therapy. If your child struggles with learning, we can assess and address whatever challenges we find—whether it's dyslexia, ADHD, processing differences, or something else entirely."
    },
    {
      question: "How do I know if this is dyslexia vs. ADHD vs. something else?",
      answer: "We help figure that out. We assess and address whatever executive function weaknesses we find. Many children have overlapping challenges that all respond well to comprehensive EF strengthening."
    },
    {
      question: "Why come to you instead of getting more tutoring?",
      answer: "Tutoring teaches content. We train the brain skills that make learning any content easier."
    },
    {
      question: "Will insurance cover this?",
      answer: "Most insurance plans cover speech-language pathology services for cognitive-communication disorders and learning-related challenges with a diagnosis. We help you verify your benefits and navigate coverage."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-playfair font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600">
            Get answers to common questions about our executive function therapy approach.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0">
                  <SafeIcon 
                    icon={openIndex === index ? FiMinus : FiPlus} 
                    className="h-5 w-5 text-accent-magenta" 
                  />
                </div>
              </button>
              <motion.div
                initial={false}
                animate={{
                  height: openIndex === index ? 'auto' : 0,
                  opacity: openIndex === index ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;